import { z } from 'zod';

export const errorResponseSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
  message: z.string().optional(),
}).passthrough();

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  googleId: z.string().nullable(),
  hasPassword: z.boolean().optional(),
  emailVerifiedAt: z.union([z.string(), z.date()]).nullable(),
  isSystemAdmin: z.boolean(),
  disabledAt: z.string().nullable(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
  licenseOverride: z.unknown().nullable(),
  /** Set at signup or with PUT /onboarding/intent. */
  onboardingIntent: z.enum(['platform', 'team']).nullable().optional(),
  onboardingUseCase: z.string().nullable().optional(),
  /** Successful AI generations used while unverified (UNVERIFIED_AI_GENERATION_ALLOWANCE = 3). */
  unverifiedAiGenerations: z.number().int().optional(),
});

export const authStatusResponseSchema = z.object({
  auth: z.object({
    googleOAuth: z.object({
      configured: z.boolean(),
      callbackUrl: z.string(),
      missing: z.array(z.string()),
    }),
  }),
});

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

const attributionValueSchema = z.string().trim().max(200).optional();

/**
 * Marketing attribution handed over from the landing site at signup: campaign-level UTM values only,
 * as the privacy policy describes. Unknown keys (including gclid, utm_term, utm_content and referrer
 * sent by older clients) are stripped and never stored.
 */
export const signupAttributionSchema = z.object({
  utm_source: attributionValueSchema,
  utm_medium: attributionValueSchema,
  utm_campaign: attributionValueSchema,
});

export const onboardingIntentSchema = z.enum(['platform', 'team']);
export const onboardingUseCaseSchema = z.string().trim().min(1).max(100);

export const registerRequestSchema = z.object({
  name: z.string().min(2).max(100),
  email: z.string().email(),
  password: z.string().min(8).max(128),
  attribution: signupAttributionSchema.optional(),
  /** Optional onboarding answers from the signup page; stored like PUT /onboarding/intent. */
  onboardingIntent: onboardingIntentSchema.optional(),
  useCase: onboardingUseCaseSchema.optional(),
});

/** Records attribution for an account created moments ago through Google or Apple sign-in. */
export const signupAttributionRequestSchema = z.object({
  attribution: signupAttributionSchema,
});

export const signupAttributionResponseSchema = z.object({ stored: z.boolean() });

export const authResponseSchema = z.object({
  token: z.string(),
  user: userSchema,
});

export const emailVerificationRequestSchema = z.object({
  token: z.string().min(32),
});

export const successResponseSchema = z.object({ success: z.literal(true) });

// ── Plan and verification errors (HTTP 403) ─────────────────────────────────

export const planTierSchema = z.enum(['PRO', 'BUSINESS', 'ENTERPRISE']);
export const planFeatureSchema = z.enum([
  'webhooks', 'apiKeys', 'sdk', 'whiteLabel', 'customDomain', 'registrationForms', 'emailDistribution', 'zapier', 'chatNotifications',
]);
export const planLimitSchema = z.enum(['surveys', 'responses', 'workspaces', 'seats', 'aiGenerations', 'emailInvites']);

/** A plan without the feature refused the request. */
export const planFeatureRequiredErrorSchema = z.object({
  error: z.string(),
  code: z.literal('PLAN_FEATURE_REQUIRED'),
  feature: planFeatureSchema,
  requiredTier: planTierSchema,
}).passthrough();

/** A plan quota is used up. `periodEnd` on monthly quotas, `limitedByTrial` on trial workspace limits. */
export const planLimitReachedErrorSchema = z.object({
  error: z.string(),
  code: z.literal('PLAN_LIMIT_REACHED'),
  limit: planLimitSchema,
  current: z.number(),
  max: z.number(),
  requiredTier: planTierSchema,
  periodEnd: z.string().optional(),
  limitedByTrial: z.boolean().optional(),
}).passthrough();

export const verifiedEmailFeatureSchema = z.enum([
  'ai', 'apiKeys', 'webhooks', 'emailDistribution', 'zapier', 'chatNotifications', 'teamInvitations',
  'responseNotifications', 'identityVerification', 'billingManagement',
]);

export const emailVerificationRequiredErrorSchema = z.object({
  error: z.string(),
  code: z.literal('EMAIL_VERIFICATION_REQUIRED'),
  feature: verifiedEmailFeatureSchema,
});

// ── Onboarding ──────────────────────────────────────────────────────────────

export const onboardingProgressQuerySchema = z.object({ workspaceId: z.string().min(1) });

export const onboardingProgressResponseSchema = z.object({
  intent: onboardingIntentSchema.nullable(),
  useCase: z.string().nullable(),
  steps: z.object({
    surveyCreated: z.boolean(),
    brandingSaved: z.boolean(),
    published: z.boolean(),
    firstResponse: z.boolean(),
    sharedOrEmbedded: z.boolean(),
    integrationConnected: z.boolean(),
  }),
  firstSurveyId: z.string().nullable(),
  firstSurveySlug: z.string().nullable(),
  publicApiKeyExists: z.boolean(),
});

export const onboardingIntentRequestSchema = z.object({
  intent: onboardingIntentSchema,
  useCase: onboardingUseCaseSchema.nullable().optional(),
});

export const onboardingIntentResponseSchema = z.object({
  intent: onboardingIntentSchema,
  useCase: z.string().nullable(),
});

// ── Workspace usage ─────────────────────────────────────────────────────────

export const responsesQuotaStateSchema = z.enum(['ok', 'warning', 'exceeded']);

/** GET /workspaces/:workspaceId/usage (fields used by the dashboard; more are returned). */
export const workspaceUsageResponseSchema = z.object({
  usage: z.object({
    tier: z.string(),
    planName: z.string(),
    responsesUsed: z.number(),
    responsesLimit: z.number().nullable(),
    /** warning at 80 % of responsesLimit or more, exceeded at 100 %. */
    responsesQuotaState: responsesQuotaStateSchema,
    aiGenerationsUsed: z.number(),
    aiGenerationsLimit: z.number().nullable(),
    periodStart: z.string(),
    periodEnd: z.string(),
  }).passthrough(),
});

// ── Public survey completion ────────────────────────────────────────────────

/** Score part of a quiz result; `passed` is null without a passing score or while answers await review. */
export const publicQuizScoreSchema = z.object({
  earnedPoints: z.number(),
  totalPoints: z.number(),
  scorePercent: z.number(),
  passed: z.boolean().nullable(),
  passingScore: z.number().nullable(),
});

export const publicQuizQuestionResultSchema = z.object({
  questionId: z.string(),
  status: z.enum(['correct', 'incorrect', 'pending']),
  /** As stored (option values); null for confidential answers. */
  givenAnswer: z.unknown(),
  /** Answer key: option values, or the reference answer for open text. */
  correctAnswer: z.unknown(),
  explanation: z.union([z.string(), z.record(z.string(), z.string())]).nullable(),
  feedback: z.string().nullable().optional(),
  earnedPoints: z.number().optional(),
  maxPoints: z.number().optional(),
});

/**
 * Quiz result for the respondent, limited by the survey's quiz settings: `score` only with
 * showScore, `questions` (answer key and explanations) only with showCorrectAnswers.
 */
export const publicQuizResultSchema = z.object({
  score: publicQuizScoreSchema.optional(),
  pendingCount: z.number().int().min(0),
  questions: z.array(publicQuizQuestionResultSchema).optional(),
  allowRetake: z.boolean(),
});

export const completePublicResponseRequestSchema = z.object({
  responseId: z.string().max(200),
  answers: z.array(z.object({ questionId: z.string().max(200), value: z.unknown() })).max(500),
});

/** POST /public/workspaces/:workspaceSlug/surveys/:slug/responses/complete */
export const completePublicResponseResponseSchema = z.object({
  success: z.literal(true),
  giftCode: z.string().optional(),
  endScreenId: z.string().optional(),
  /** Present for quiz surveys that show the score or the correct answers. */
  quiz: publicQuizResultSchema.optional(),
});

// ── Quiz results for workspace members ─────────────────────────────────────

export const adminQuizStatusSchema = z.enum(['passed', 'failed', 'pending_review', 'scored']);
/** `?quizStatus=` of GET /surveys/:surveyId/responses and the export endpoints. */
export const adminQuizStatusFilterSchema = z.enum(['passed', 'failed', 'pending_review']);
/** `?sort=` of GET /surveys/:surveyId/responses. */
export const responseListSortSchema = z.enum(['newest', 'score_desc', 'score_asc']);

/**
 * Score summary of one response (the `quiz` field of a Responses list item). Shown for anonymous
 * responses too; never carries answers. `passed` is null without a passing score or while answers
 * awaiting review could change the outcome.
 */
export const adminQuizSummarySchema = z.object({
  score: z.number(),
  maxScore: z.number(),
  scorePercent: z.number(),
  passed: z.boolean().nullable(),
  pendingReview: z.number().int().min(0),
  status: adminQuizStatusSchema,
});

export const adminQuizQuestionResultSchema = z.object({
  questionId: z.string(),
  label: z.string(),
  questionType: z.string().nullable(),
  /** `confidential`: a confidential answer the viewer may not see; answer, correctness and points are withheld. */
  status: z.enum(['correct', 'incorrect', 'pending', 'confidential']),
  earnedPoints: z.number().nullable(),
  maxPoints: z.number(),
  givenAnswer: z.unknown(),
  givenAnswerText: z.string().nullable(),
  correctAnswer: z.unknown(),
  correctAnswerText: z.string().nullable(),
  explanation: z.string().nullable(),
  aiFeedback: z.string().nullable(),
  evaluatedBy: z.enum(['auto', 'ai', 'manual', 'pending']),
  reviewedByName: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  canGrade: z.boolean(),
});

export const adminQuizResultSchema = adminQuizSummarySchema.extend({
  responseId: z.string(),
  passingScore: z.number().nullable(),
  evaluatedBy: z.enum(['auto', 'ai', 'mixed']),
  evaluatedAt: z.string(),
  questions: z.array(adminQuizQuestionResultSchema),
});

/** GET /responses/:responseId/quiz — null for a response without a stored quiz result. */
export const responseQuizResponseSchema = z.object({ quiz: adminQuizResultSchema.nullable() });

/** PUT /responses/:responseId/quiz/questions/:questionId/grade */
export const quizGradeRequestSchema = z.object({
  correct: z.boolean(),
  /** Whole points from 0 to the question's points; defaults to all points when correct and none when incorrect. */
  points: z.number().int().min(0).max(1000).optional(),
});

export const quizScoreBucketSchema = z.object({
  from: z.number(),
  to: z.number(),
  label: z.string(),
  count: z.number().int().min(0),
});

export const quizQuestionDifficultySchema = z.object({
  questionId: z.string(),
  label: z.string(),
  graded: z.number().int().min(0),
  correct: z.number().int().min(0),
  correctRate: z.number(),
  pending: z.number().int().min(0),
});

/** The `quiz` field of GET /surveys/:surveyId/analytics (quiz surveys only). */
export const quizAnalyticsSummarySchema = z.object({
  gradedResponses: z.number().int().min(0),
  averageScorePercent: z.number().nullable(),
  averageScore: z.number().nullable(),
  maxScore: z.number().nullable(),
  passingScore: z.number().nullable(),
  passed: z.number().int().min(0),
  failed: z.number().int().min(0),
  pendingReview: z.number().int().min(0),
  passRate: z.number().nullable(),
  distribution: z.array(quizScoreBucketSchema),
  hardestQuestions: z.array(quizQuestionDifficultySchema),
});

/** GET /surveys/:surveyId/responses query (filters are shared with the export endpoints). */
export const responseListQuerySchema = z.object({
  page: z.string().optional(),
  pageSize: z.string().optional(),
  status: z.enum(['completed']).optional(),
  includeHidden: z.enum(['true', 'false']).optional(),
  search: z.string().max(200).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  quizStatus: adminQuizStatusFilterSchema.optional(),
  sort: responseListSortSchema.optional(),
});

/** GET /surveys/:surveyId/responses (fields relevant to quizzes; every response carries more). */
export const responseListResponseSchema = z.object({
  responses: z.array(z.object({ id: z.string(), quiz: adminQuizSummarySchema.optional() }).passthrough()),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  sort: responseListSortSchema,
});

/** GET /surveys/:surveyId/analytics (the quiz part; more fields are returned). */
export const surveyAnalyticsResponseSchema = z.object({
  analytics: z.object({ quiz: quizAnalyticsSummarySchema.nullable() }).passthrough(),
});

// ── Branding ────────────────────────────────────────────────────────────────

/** POST /workspaces/:workspaceId/branding/logo (multipart/form-data, one png/jpeg/webp file up to 1 MB). */
export const brandingLogoResponseSchema = z.object({ logoUrl: z.string().url() });

export const LOGO_UPLOAD_MAX_BYTES = 1024 * 1024;
export const LOGO_UPLOAD_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const healthResponseSchema = z.object({
  status: z.enum(['ok', 'ready']),
  timestamp: z.string(),
});

export interface ApiContract<
  TMethod extends 'GET' | 'POST' | 'PUT',
  TPath extends string,
  TRequest extends z.ZodTypeAny | undefined,
  TResponse extends z.ZodTypeAny,
> {
  method: TMethod;
  path: TPath;
  request: TRequest;
  response: TResponse;
}

function contract<
  TMethod extends 'GET' | 'POST' | 'PUT',
  TPath extends string,
  TRequest extends z.ZodTypeAny | undefined,
  TResponse extends z.ZodTypeAny,
>(definition: ApiContract<TMethod, TPath, TRequest, TResponse>) {
  return definition;
}

export const apiContracts = {
  authStatus: contract({ method: 'GET', path: '/auth/status', request: undefined, response: authStatusResponseSchema }),
  login: contract({ method: 'POST', path: '/auth/login', request: loginRequestSchema, response: authResponseSchema }),
  register: contract({ method: 'POST', path: '/auth/register', request: registerRequestSchema, response: authResponseSchema }),
  signupAttribution: contract({ method: 'POST', path: '/auth/me/attribution', request: signupAttributionRequestSchema, response: signupAttributionResponseSchema }),
  confirmEmail: contract({ method: 'POST', path: '/auth/verify-email/confirm', request: emailVerificationRequestSchema, response: successResponseSchema }),
  onboardingProgress: contract({ method: 'GET', path: '/onboarding/progress', request: undefined, response: onboardingProgressResponseSchema }),
  onboardingIntent: contract({ method: 'PUT', path: '/onboarding/intent', request: onboardingIntentRequestSchema, response: onboardingIntentResponseSchema }),
  health: contract({ method: 'GET', path: '/health', request: undefined, response: healthResponseSchema }),
  ready: contract({ method: 'GET', path: '/ready', request: undefined, response: healthResponseSchema }),
} as const;

export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type RegisterRequest = z.infer<typeof registerRequestSchema>;
export type SignupAttributionInput = z.infer<typeof signupAttributionSchema>;
export type AuthResponse = z.infer<typeof authResponseSchema>;
export type AuthStatusResponse = z.infer<typeof authStatusResponseSchema>;
export type PlanFeatureRequiredErrorBody = z.infer<typeof planFeatureRequiredErrorSchema>;
export type PlanLimitReachedErrorBody = z.infer<typeof planLimitReachedErrorSchema>;
export type EmailVerificationRequiredErrorBody = z.infer<typeof emailVerificationRequiredErrorSchema>;
export type OnboardingProgressResponse = z.infer<typeof onboardingProgressResponseSchema>;
export type OnboardingIntentRequest = z.infer<typeof onboardingIntentRequestSchema>;
export type WorkspaceUsageResponse = z.infer<typeof workspaceUsageResponseSchema>;
export type PublicQuizResultContract = z.infer<typeof publicQuizResultSchema>;
export type CompletePublicResponseResponse = z.infer<typeof completePublicResponseResponseSchema>;
export type AdminQuizSummaryContract = z.infer<typeof adminQuizSummarySchema>;
export type AdminQuizResultContract = z.infer<typeof adminQuizResultSchema>;
export type QuizGradeRequestContract = z.infer<typeof quizGradeRequestSchema>;
export type QuizAnalyticsSummaryContract = z.infer<typeof quizAnalyticsSummarySchema>;

export async function requestContract<TContract extends ApiContract<'GET' | 'POST' | 'PUT', string, z.ZodTypeAny | undefined, z.ZodTypeAny>>(
  baseUrl: string,
  definition: TContract,
  input?: TContract['request'] extends z.ZodTypeAny ? z.input<TContract['request']> : never,
  init?: Omit<RequestInit, 'body' | 'method'>,
): Promise<z.output<TContract['response']>> {
  const body = definition.request ? definition.request.parse(input) : undefined;
  const response = await fetch(`${baseUrl}${definition.path}`, {
    ...init,
    method: definition.method,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...init?.headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload: unknown = await response.json();
  if (!response.ok) {
    const parsed = errorResponseSchema.safeParse(payload);
    throw new Error(parsed.success ? parsed.data.error : `API request failed with HTTP ${response.status}`);
  }
  return definition.response.parse(payload) as z.output<TContract['response']>;
}

export { generateOpenApiDocument } from './openapi.js';
